import {
  CodeBlock,
  CodeBlockCopyButton,
} from "@/components/ai-elements/code-block";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import {
  AgentCallTelemetry,
  SearchEngineResult,
  SearchTelemetryData,
} from "@shaarp/common";
import {
  Braces,
  Brain,
  Check,
  Copy,
  Download,
  Filter,
  Loader2,
  TriangleAlert,
  Wrench,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type SearchEngineDebugModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchId: string | undefined;
  searchEngineResult: SearchEngineResult | undefined;
  isSearching: boolean;
};

/** Human-readable label for an orchestrator tool name (LLM-side identifier). */
const TOOL_LABELS: Record<string, string> = {
  search_companies_pappers: "Pappers (registre FR)",
  search_companies_scrapio: "ScrapIO (Google Maps)",
  search_companies_coresignal: "Coresignal (B2B)",
  search_companies_coresignal_jobs: "Coresignal Jobs (offres)",
  search_employees_coresignal: "Coresignal (contacts)",
  search_companies_web: "Recherche Web",
  search_companies_web_deep: "Recherche approfondie",
  search_companies_gouv: "API Gouv (SIRENE)",
};

/** Human-readable label for an executed agent type (runtime identifier). */
const AGENT_LABELS: Record<string, string> = {
  orchestrator: "Orchestrateur",
  coresignal: "Coresignal (B2B)",
  coresignal_jobs: "Coresignal Jobs",
  websearch: "Recherche Web",
  apolloio: "Apollo.io",
  pappers: "Pappers",
  gouv: "API Gouv (SIRENE)",
  scrapio: "ScrapIO (Google Maps)",
  database: "Base locale",
  employee: "Contacts",
};

const toolLabel = (tool: string): string => TOOL_LABELS[tool] ?? tool;
const agentLabel = (type: string): string => AGENT_LABELS[type] ?? type;

/** Format a millisecond duration as ms (< 1s) or seconds (>= 1s). */
const formatMs = (ms?: number): string => {
  if (ms == null) return "—";
  return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`;
};

const formatUsd = (n?: number): string =>
  n == null ? "—" : `$${n.toFixed(4)}`;

const AMBIGUITY_VARIANT: Record<
  string,
  "success" | "warning" | "destructive"
> = {
  low: "success",
  medium: "warning",
  high: "destructive",
};

/** Escape `|` so free text stays inside a Markdown table cell. */
const escapeCell = (s: string): string => s.replace(/\|/g, "\\|");

/**
 * Serialize the whole debug snapshot into a self-contained Markdown report —
 * intent, executed agents, filtering pipeline and a raw-JSON appendix. Shared
 * by the "copy context" and "download .md" actions so an AI assistant (or a
 * human) gets the full picture from a single paste/file.
 */
function buildDebugMarkdown(
  telemetry: SearchTelemetryData | null,
  searchEngineResult: SearchEngineResult | undefined,
): string {
  const lines: string[] = [];
  const push = (line = "") => lines.push(line);

  const query = telemetry?.query ?? "";
  push(`# Engine Debug${query ? ` — "${query}"` : ""}`);
  push();

  if (telemetry) {
    push(`- **Status:** ${telemetry.status}`);
    if (telemetry.searchId) push(`- **Search ID:** \`${telemetry.searchId}\``);
    push(`- **Search mode:** ${telemetry.searchMode ?? "—"}`);
    push(
      `- **Forced tool:** ${
        telemetry.forcedTool
          ? `${toolLabel(telemetry.forcedTool)} (\`${telemetry.forcedTool}\`)`
          : "auto"
      }`,
    );
    push(`- **Page:** ${telemetry.page}`);
    push(
      `- **Timing:** total ${formatMs(telemetry.totalMs)}${
        telemetry.intentAnalysis?.declared_at_ms != null
          ? ` · reasoning ${formatMs(telemetry.intentAnalysis.declared_at_ms)}`
          : ""
      } · orchestrator ${formatMs(
        telemetry.orchestratorMs,
      )} · enrichment ${formatMs(telemetry.enrichmentMs)}`,
    );
    if (telemetry.orchestratorModel)
      push(`- **Model:** ${telemetry.orchestratorModel}`);
    if (telemetry.cost)
      push(
        `- **Cost:** ${formatUsd(telemetry.cost.total_cost_usd)} (LLM ${formatUsd(
          telemetry.cost.llm_cost_usd,
        )} + provider ${formatUsd(telemetry.cost.provider_cost_usd)})`,
      );
    if (telemetry.errorMessage)
      push(`- **Error:** ${telemetry.errorMessage}`);
    push();

    const intent = telemetry.intentAnalysis;
    push(`## Intent analysis`);
    if (intent) {
      push(`- **Summary:** ${intent.intent_summary}`);
      push(`- **Category:** ${intent.intent_category}`);
      push(`- **Ambiguity:** ${intent.ambiguity_level}`);
      push(`- **Detected signals:** ${intent.detected_signals.join(", ")}`);
      push();
      push(`### Planned tools (pre-flight)`);
      intent.chosen_tools_in_order.forEach((c, i) =>
        push(`${i + 1}. **${toolLabel(c.tool)}** (\`${c.tool}\`) — ${c.reason}`),
      );
      push();
      if (intent.rejected_tools.length > 0) {
        push(`### Rejected tools`);
        intent.rejected_tools.forEach((r) =>
          push(`- **${toolLabel(r.tool)}** (\`${r.tool}\`) — ${r.reason}`),
        );
        push();
      }
    } else {
      push(`_No intent analysis recorded._`);
      push();
    }

    push(`## Executed agents`);
    if (telemetry.agents.length > 0) {
      telemetry.agents.forEach((a, i) => {
        push(`### ${i + 1}. ${agentLabel(a.agentType)} (\`${a.agentType}\`)`);
        const meta =
          a.durationMs != null
            ? [
                `results: ${a.resultsCount ?? 0}`,
                `duration: ${formatMs(a.durationMs)}`,
              ]
            : [
                "⚠ did not complete — no completion recorded (likely failed or aborted; error not captured)",
              ];
        if (a.cacheHit) meta.push("cache hit");
        if (a.fromFallback) meta.push("fallback");
        if (a.filteredOutCount != null)
          meta.push(`filtered out: ${a.filteredOutCount}`);
        push(`- ${meta.join(" · ")}`);
        if (a.errors && a.errors.length > 0)
          push(`- Errors: ${a.errors.join("; ")}`);
        if (a.params && Object.keys(a.params).length > 0) {
          push(`- Params:`);
          push("```json");
          push(JSON.stringify(a.params, null, 2));
          push("```");
        }
        if (a.esDslQuery != null) {
          push(`- ES DSL query:`);
          push("```json");
          push(JSON.stringify(a.esDslQuery, null, 2));
          push("```");
        }
        push();
      });
    } else {
      push(`_No agent executed._`);
      push();
    }

    push(`## Filtering`);
    push(`- Emitted: ${telemetry.counters.companiesEmitted}`);
    push(`- Rejected: ${telemetry.counters.companiesRejectedByFilter}`);
    push(`- Agents called: ${telemetry.counters.agentsCalled}`);
    push(`- API errors: ${telemetry.counters.apiErrors}`);
    push(
      `- Cache: ${telemetry.counters.cacheHits} hits / ${telemetry.counters.cacheMisses} misses`,
    );
    push();

    const breakdown = new Map<string, { accepted: number; rejected: number }>();
    for (const d of telemetry.filterDecisions) {
      const cur = breakdown.get(d.filterName) ?? { accepted: 0, rejected: 0 };
      if (d.accepted) cur.accepted++;
      else cur.rejected++;
      breakdown.set(d.filterName, cur);
    }
    if (breakdown.size > 0) {
      push(`### Applied filters`);
      for (const [name, c] of breakdown)
        push(`- \`${name}\`: +${c.accepted} accepted / −${c.rejected} rejected`);
      push();
    }

    const rejections = telemetry.filterDecisions.filter((d) => !d.accepted);
    if (rejections.length > 0) {
      push(`### Rejection details`);
      push(`| Company | Filter | Reason |`);
      push(`| --- | --- | --- |`);
      for (const d of rejections)
        push(
          `| ${escapeCell(d.companyName ?? d.companyId)} | \`${d.filterName}\` | ${escapeCell(
            d.reason ?? "—",
          )} |`,
        );
      push();
    }
  } else {
    push(
      `_Detailed telemetry unavailable (search in progress, expired from cache, or ran on another process)._`,
    );
    push();
  }

  push(`## Raw JSON`);
  push("```json");
  push(JSON.stringify(telemetry ?? searchEngineResult ?? {}, null, 2));
  push("```");

  return lines.join("\n");
}

/** Small label / value row used across the overview tab. */
function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function SectionTitle({
  icon: Icon,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-2 items-center mb-2 text-sm font-semibold">
      <Icon className="w-4 h-4 text-muted-foreground" />
      {children}
    </div>
  );
}

/** One executed agent: type, results, timing, params and any errors. */
function AgentCard({ agent }: { agent: AgentCallTelemetry }) {
  const { t } = useTranslation();
  const params = agent.params ?? {};
  const hasParams = Object.keys(params).length > 0;
  // A missing `durationMs` means `endAgentCall` never ran: the agent started
  // but never reported back — it most likely threw or was aborted mid-flight.
  // Surfacing this distinguishes a genuine "source returned 0" from a silent
  // failure (the actual cause of most unexplained 0-result searches).
  const hasCompleted = agent.durationMs != null;

  return (
    <div className="p-3 space-y-2 rounded-lg border bg-muted/30">
      <div className="flex flex-wrap gap-2 items-center">
        <Badge variant="secondary">{agentLabel(agent.agentType)}</Badge>
        {hasCompleted ? (
          <>
            <Badge variant="outline">
              {agent.resultsCount ?? 0} résultat
              {(agent.resultsCount ?? 0) > 1 && "s"}
            </Badge>
            <Badge variant="outline">{formatMs(agent.durationMs)}</Badge>
          </>
        ) : (
          <Badge variant="warning">
            {t("prospecting.search.engine.tools.agentIncomplete")}
          </Badge>
        )}
        {agent.cacheHit && <Badge variant="outline">cache</Badge>}
        {agent.fromFallback && <Badge variant="warning">fallback</Badge>}
        {agent.filteredOutCount != null && agent.filteredOutCount > 0 && (
          <Badge variant="outline">{agent.filteredOutCount} filtrés</Badge>
        )}
        {agent.errors && agent.errors.length > 0 && (
          <Badge variant="destructive">{agent.errors.length} erreur(s)</Badge>
        )}
      </div>
      {!hasCompleted && !(agent.errors && agent.errors.length > 0) && (
        <p className="text-xs text-muted-foreground">
          {t("prospecting.search.engine.tools.agentIncompleteHint")}
        </p>
      )}
      {agent.errors && agent.errors.length > 0 && (
        <ul className="pl-4 text-xs list-disc text-destructive">
          {agent.errors.map((err, i) => (
            <li key={i}>{err}</li>
          ))}
        </ul>
      )}
      {hasParams && (
        <CodeBlock code={JSON.stringify(params, null, 2)} language="json">
          <CodeBlockCopyButton />
        </CodeBlock>
      )}
      {agent.esDslQuery != null && (
        <CodeBlock
          code={JSON.stringify(agent.esDslQuery, null, 2)}
          language="json"
        >
          <CodeBlockCopyButton />
        </CodeBlock>
      )}
    </div>
  );
}

/**
 * Admin-only debug modal for the search engine. Surfaces the rich telemetry
 * snapshot (intent analysis, tools chosen/rejected, executed agents, and the
 * filtering pipeline) behind the `GET /search-engine/debug/:searchId` endpoint,
 * falling back to the live SSE result when the snapshot is unavailable.
 */
export default function SearchEngineDebugModal({
  open,
  onOpenChange,
  searchId,
  searchEngineResult,
  isSearching,
}: SearchEngineDebugModalProps) {
  const { t } = useTranslation();
  const api = useApi();

  const [telemetry, setTelemetry] = useState<SearchTelemetryData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUnavailable, setIsUnavailable] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!searchId) {
      setTelemetry(null);
      setIsUnavailable(true);
      return;
    }
    setIsLoading(true);
    setIsUnavailable(false);
    api
      .get<SearchTelemetryData>(`/search-engine/debug/${searchId}`)
      .then((res) => {
        setTelemetry(res.data);
        setIsUnavailable(false);
      })
      .catch(() => {
        setTelemetry(null);
        setIsUnavailable(true);
      })
      .finally(() => setIsLoading(false));
  }, [open, searchId, api]);

  const intent = telemetry?.intentAnalysis;
  const rejections = useMemo(
    () => telemetry?.filterDecisions.filter((d) => !d.accepted) ?? [],
    [telemetry],
  );
  const filterBreakdown = useMemo(() => {
    const map = new Map<string, { accepted: number; rejected: number }>();
    for (const d of telemetry?.filterDecisions ?? []) {
      const cur = map.get(d.filterName) ?? { accepted: 0, rejected: 0 };
      if (d.accepted) cur.accepted++;
      else cur.rejected++;
      map.set(d.filterName, cur);
    }
    return Array.from(map.entries());
  }, [telemetry]);

  // Agents to show in the "Outils" tab: prefer telemetry, fall back to the
  // live SSE result (non-orchestrator agents) when the snapshot is missing.
  const fallbackAgents = useMemo(
    () =>
      (searchEngineResult?.searchEngineAgents ?? []).filter(
        (a) => a.type !== "orchestrator",
      ),
    [searchEngineResult],
  );

  const rawJson = telemetry ?? searchEngineResult ?? {};

  const handleCopyContext = () => {
    navigator.clipboard
      .writeText(buildDebugMarkdown(telemetry, searchEngineResult))
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      })
      .catch(() => undefined);
  };

  const handleDownloadMarkdown = () => {
    const md = buildDebugMarkdown(telemetry, searchEngineResult);
    const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `engine-debug-${telemetry?.searchId ?? searchId ?? "result"}.md`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap gap-2 items-center">
            {t("prospecting.search.engine.title")}
            {telemetry?.status && (
              <Badge
                variant={
                  telemetry.status === "completed"
                    ? "success"
                    : telemetry.status === "error"
                      ? "destructive"
                      : "secondary"
                }
              >
                {telemetry.status}
              </Badge>
            )}
          </DialogTitle>
          {telemetry?.query && (
            <p className="text-sm truncate text-muted-foreground">
              {telemetry.query}
            </p>
          )}
        </DialogHeader>

        {!isSearching && (
          <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyContext}
            disabled={isLoading}
          >
            {isCopied ? <Check className="text-success" /> : <Copy />}
            {isCopied
              ? t("prospecting.search.engine.copied")
              : t("prospecting.search.engine.copyContext")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadMarkdown}
            disabled={isLoading}
          >
            <Download />
            {t("prospecting.search.engine.downloadMd")}
          </Button>
          </div>
        )}

        {isLoading ? (
          <div className="flex flex-1 gap-2 justify-center items-center py-16 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t("prospecting.search.engine.loading")}
          </div>
        ) : (
          <Tabs defaultValue="overview" className="flex flex-col min-h-0">
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="overview" className="gap-1.5">
                <Brain className="w-3.5 h-3.5" />
                {t("prospecting.search.engine.tabs.overview")}
              </TabsTrigger>
              <TabsTrigger value="tools" className="gap-1.5">
                <Wrench className="w-3.5 h-3.5" />
                {t("prospecting.search.engine.tabs.tools")}
              </TabsTrigger>
              <TabsTrigger value="filtering" className="gap-1.5">
                <Filter className="w-3.5 h-3.5" />
                {t("prospecting.search.engine.tabs.filtering")}
              </TabsTrigger>
              <TabsTrigger value="json" className="gap-1.5">
                <Braces className="w-3.5 h-3.5" />
                {t("prospecting.search.engine.tabs.json")}
              </TabsTrigger>
            </TabsList>

            {isUnavailable && (
              <div className="flex gap-2 items-center px-3 py-2 mt-2 text-xs rounded-md border border-warning text-foreground bg-warning/10">
                <TriangleAlert className="w-3.5 h-3.5 shrink-0 text-warning" />
                {t("prospecting.search.engine.unavailable")}
              </div>
            )}

            {/* ── Aperçu ───────────────────────────────────────────── */}
            <TabsContent
              value="overview"
              className="overflow-auto max-h-[60vh] pr-1 space-y-4"
            >
              {intent ? (
                <>
                  <div>
                    <SectionTitle icon={Brain}>
                      {t("prospecting.search.engine.intent.title")}
                    </SectionTitle>
                    <p className="text-sm">{intent.intent_summary}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={t("prospecting.search.engine.intent.category")}>
                      <Badge variant="outline">{intent.intent_category}</Badge>
                    </Field>
                    <Field
                      label={t("prospecting.search.engine.intent.ambiguity")}
                    >
                      <Badge
                        variant={
                          AMBIGUITY_VARIANT[intent.ambiguity_level] ?? "outline"
                        }
                      >
                        {intent.ambiguity_level}
                      </Badge>
                    </Field>
                  </div>
                  <Field label={t("prospecting.search.engine.intent.signals")}>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {intent.detected_signals.map((s, i) => (
                        <Badge key={i} variant="secondary">
                          {s}
                        </Badge>
                      ))}
                    </div>
                  </Field>
                </>
              ) : (
                <p className="py-6 text-sm text-center text-muted-foreground">
                  {t("prospecting.search.engine.intent.empty")}
                </p>
              )}

              {telemetry && (
                <div>
                  <SectionTitle icon={Brain}>
                    {t("prospecting.search.engine.metrics.title")}
                  </SectionTitle>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <Field
                      label={t("prospecting.search.engine.metrics.searchMode")}
                    >
                      <Badge
                        variant={
                          telemetry.searchMode === "broad"
                            ? "warning"
                            : "secondary"
                        }
                      >
                        {t(
                          telemetry.searchMode === "broad"
                            ? "prospecting.search.engine.searchMode.broad"
                            : "prospecting.search.engine.searchMode.strict",
                        )}
                      </Badge>
                    </Field>
                    <Field
                      label={t("prospecting.search.engine.metrics.forcedTool")}
                    >
                      {telemetry.forcedTool ? (
                        <Badge variant="outline">
                          {toolLabel(telemetry.forcedTool)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">
                          {t("prospecting.search.engine.searchMode.auto")}
                        </span>
                      )}
                    </Field>
                    <Field label={t("prospecting.search.engine.metrics.total")}>
                      {formatMs(telemetry.totalMs)}
                    </Field>
                    {intent?.declared_at_ms != null && (
                      <Field
                        label={t("prospecting.search.engine.metrics.reasoning")}
                      >
                        {formatMs(intent.declared_at_ms)}
                      </Field>
                    )}
                    <Field
                      label={t("prospecting.search.engine.metrics.orchestrator")}
                    >
                      {formatMs(telemetry.orchestratorMs)}
                    </Field>
                    <Field
                      label={t("prospecting.search.engine.metrics.enrichment")}
                    >
                      {formatMs(telemetry.enrichmentMs)}
                    </Field>
                    <Field label={t("prospecting.search.engine.metrics.model")}>
                      <span className="font-mono text-xs">
                        {telemetry.orchestratorModel ?? "—"}
                      </span>
                    </Field>
                    {telemetry.cost && (
                      <Field label={t("prospecting.search.engine.metrics.cost")}>
                        {formatUsd(telemetry.cost.total_cost_usd)}
                      </Field>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* ── Outils ───────────────────────────────────────────── */}
            <TabsContent
              value="tools"
              className="overflow-auto max-h-[60vh] pr-1 space-y-4"
            >
              {intent && intent.chosen_tools_in_order.length > 0 && (
                <div>
                  <SectionTitle icon={Check}>
                    {t("prospecting.search.engine.tools.chosen")}
                  </SectionTitle>
                  {intent.detected_signals.includes("classifier_error") && (
                    <div className="flex gap-2 items-center px-3 py-2 mb-2 text-xs rounded-md border border-warning text-foreground bg-warning/10">
                      <TriangleAlert className="w-3.5 h-3.5 shrink-0 text-warning" />
                      {t("prospecting.search.engine.tools.classifierFailed")}
                    </div>
                  )}
                  <ol className="space-y-1.5">
                    {intent.chosen_tools_in_order.map((c, i) => (
                      <li
                        key={i}
                        className="flex gap-2 items-start p-2 rounded-md border bg-success/5"
                      >
                        <span className="flex justify-center items-center w-5 h-5 text-xs font-semibold rounded-full shrink-0 bg-success text-success-foreground">
                          {i + 1}
                        </span>
                        <div>
                          <span className="text-sm font-medium">
                            {toolLabel(c.tool)}
                          </span>
                          <p className="text-xs text-muted-foreground">
                            {c.reason}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {intent && intent.rejected_tools.length > 0 && (
                <div>
                  <SectionTitle icon={X}>
                    {t("prospecting.search.engine.tools.rejected")}
                  </SectionTitle>
                  <ul className="space-y-1.5">
                    {intent.rejected_tools.map((r, i) => (
                      <li
                        key={i}
                        className="flex gap-2 items-start p-2 rounded-md border"
                      >
                        <X className="mt-0.5 w-4 h-4 shrink-0 text-muted-foreground" />
                        <div>
                          <span className="text-sm font-medium text-muted-foreground line-through">
                            {toolLabel(r.tool)}
                          </span>
                          <p className="text-xs text-muted-foreground">
                            {r.reason}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <SectionTitle icon={Wrench}>
                  {t("prospecting.search.engine.tools.executed")}
                </SectionTitle>
                {telemetry && telemetry.agents.length > 0 ? (
                  <div className="space-y-2">
                    {telemetry.agents.map((agent) => (
                      <AgentCard key={agent.agentId} agent={agent} />
                    ))}
                  </div>
                ) : fallbackAgents.length > 0 ? (
                  <div className="space-y-2">
                    {fallbackAgents.map((agent) => (
                      <div
                        key={agent.id}
                        className="p-3 space-y-2 rounded-lg border bg-muted/30"
                      >
                        <div className="flex flex-wrap gap-2 items-center">
                          <Badge variant="secondary">
                            {agentLabel(agent.type)}
                          </Badge>
                          <Badge variant="outline">
                            {agent.resultsCount} résultat
                            {agent.resultsCount > 1 && "s"}
                          </Badge>
                        </div>
                        <CodeBlock
                          code={JSON.stringify(agent.properties, null, 2)}
                          language="json"
                        >
                          <CodeBlockCopyButton />
                        </CodeBlock>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="py-6 text-sm text-center text-muted-foreground">
                    {t("prospecting.search.engine.tools.empty")}
                  </p>
                )}
              </div>
            </TabsContent>

            {/* ── Filtrage ─────────────────────────────────────────── */}
            <TabsContent
              value="filtering"
              className="overflow-auto max-h-[60vh] pr-1 space-y-4"
            >
              {telemetry ? (
                <>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <Field
                      label={t("prospecting.search.engine.filtering.emitted")}
                    >
                      <span className="font-semibold text-success">
                        {telemetry.counters.companiesEmitted}
                      </span>
                    </Field>
                    <Field
                      label={t("prospecting.search.engine.filtering.rejected")}
                    >
                      <span className="font-semibold text-destructive">
                        {telemetry.counters.companiesRejectedByFilter}
                      </span>
                    </Field>
                    <Field
                      label={t("prospecting.search.engine.filtering.agents")}
                    >
                      {telemetry.counters.agentsCalled}
                    </Field>
                    <Field
                      label={t("prospecting.search.engine.filtering.apiErrors")}
                    >
                      {telemetry.counters.apiErrors}
                    </Field>
                  </div>

                  {filterBreakdown.length > 0 && (
                    <div>
                      <SectionTitle icon={Filter}>
                        {t("prospecting.search.engine.filtering.breakdown")}
                      </SectionTitle>
                      <div className="flex flex-wrap gap-2">
                        {filterBreakdown.map(([name, c]) => (
                          <div
                            key={name}
                            className="flex gap-2 items-center px-2.5 py-1 text-xs rounded-md border"
                          >
                            <span className="font-medium">{name}</span>
                            {c.accepted > 0 && (
                              <span className="text-success">
                                +{c.accepted}
                              </span>
                            )}
                            {c.rejected > 0 && (
                              <span className="text-destructive">
                                −{c.rejected}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {rejections.length > 0 && (
                    <div>
                      <SectionTitle icon={X}>
                        {t("prospecting.search.engine.filtering.rejections")}
                      </SectionTitle>
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>
                                {t(
                                  "prospecting.search.engine.filtering.colCompany",
                                )}
                              </TableHead>
                              <TableHead>
                                {t(
                                  "prospecting.search.engine.filtering.colFilter",
                                )}
                              </TableHead>
                              <TableHead>
                                {t(
                                  "prospecting.search.engine.filtering.colReason",
                                )}
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {rejections.map((d, i) => (
                              <TableRow key={`${d.companyId}-${i}`}>
                                <TableCell className="font-medium">
                                  {d.companyName ?? d.companyId}
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline">
                                    {d.filterName}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground">
                                  {d.reason ?? "—"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="py-6 text-sm text-center text-muted-foreground">
                  {t("prospecting.search.engine.filtering.empty")}
                </p>
              )}
            </TabsContent>

            {/* ── JSON brut ────────────────────────────────────────── */}
            <TabsContent value="json" className="overflow-auto max-h-[60vh]">
              <CodeBlock
                code={JSON.stringify(rawJson, null, 2)}
                language="json"
              >
                <CodeBlockCopyButton />
              </CodeBlock>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
